import { useTranslation } from "react-i18next";
import { useFetch } from "../../../hooks/useFecth";
import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import { AppContext } from "../../../context/AppContext";

import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import HandlerApp from "../../../utils/handlerApp";
import BoldSelect from "../../../utils/BoldSelect";

const ActionLog = () => {
    const endpoint = "actionlog";
    const parent = "Logs";
    const prefix = "actionlog";

    const [t] = useTranslation("global");
    const api = useFetch();
    const nav = useNavigate();
    const handlerApp = HandlerApp();

    const { app } = useContext(AppContext);

    const [permissions, setPermissions] = useState([]);
    const [menus, setMenus] = useState([]);

    const title = t('actionLog.title');
    const sub_title = t('actionLog.sub_title');

    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    const [filterSerial, setFilterSerial] = useState('');
    const [filterType, setFilterType] = useState('');
    const [searchText, setSearchText] = useState('');

    const [filters, setFilters] = useState({
        serial: [],
        type: []
    });


    const columns = [
        {
            id: 'actionlog_id',
            name: 'id',
            selector: row => row.actionlog_id,
            sortable: true,
            width: "100px"
        },
        {
            id: 'machine_serial',
            name: t('actionLog.machine'),
            selector: row => row.machine_serial + " - " + (row.machine_label || 'N/A'),
            sortable: true
        },
        {
            id: 'actionlog_type',
            name: t('actionLog.type'),
            selector: row => row.actionlog_type,
            sortable: true
        },
        {
            id: 'actionlog_event',
            name: t('actionLog.event'),
            selector: row => row.actionlog_event,
            sortable: true
        },
        {
            name: t("action"),
            cell: (row) => { return handlerApp.actions(row, permissions, prefix, handlerAction) },
            ignoreRowClick: true,
            button: true,
            minWidth: "160px"
        }
    ];

    useEffect(() => {
        setMenus(app.menus[parent]);
        setPermissions(app.permissions[endpoint]);
    }, [app]);

    useEffect(() => {
        loadFilters();
        sendRequest(1);
    }, []);

    useEffect(() => {
        sendRequest(1);
    }, [filterSerial, filterType]);

    const handleSearch = () => {
        sendRequest(1);
    };

    const handlerAction = (id, action) => {
        switch (action) {
            case 'create':
                nav("/" + endpoint + "/create");
                break;
            case 'view':
                nav("/" + endpoint + "/view/" + id);
                break;
            case 'update':
                nav("/" + endpoint + "/edit/" + id);
                break;
            case 'delete':
                deleteRow(id, action);
                break;
            case 'inactive':
                optionRequest(id, action);
                break;
            case 'active':
                optionRequest(id, action);
                break;
            case 'refresh':
                sendRequest();
                break;
            default:
                break;
        }
    }

    const optionRequest = (id, action) => {
        const confirmButtonColor = action === 'active' ? 'green' : 'red';
        const confirmationMessage = action === 'active' ? t('activateAlert.activateRecord') : t('activateAlert.deactivateRecord');
        Swal.fire({
            title: confirmationMessage,
            showDenyButton: false,
            showCancelButton: true,
            confirmButtonText: t('activateAlert.confirm'),
            confirmButtonColor: confirmButtonColor,
            cancelButtonText: t('cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                const status = action === 'active' ? 1 : 0;
                const body = { action: 'updateStatus', status };
                handlerApp.setLoader(true);
                api.put(endpoint + "/" + id, body)
                    .then(response => {
                        if (response.success) {
                            handlerApp.showOk(response);
                            sendRequest();
                        } else {
                            handlerApp.handlerResponse(response);
                        }
                    })
                    .catch(error => {
                        handlerApp.showError(t(error.message));
                    })
                    .finally(() => {
                        handlerApp.setLoader(false);
                    });
            }
        });
    };

    const deleteRow = (id) => {
        Swal.fire({
            title: `${t('deleteAlert.deleteRecord')}`,
            showDenyButton: false,
            showCancelButton: true,
            confirmButtonText: `${t('deleteAlert.delete')}`,
            confirmButtonColor: "red",
            cancelButtonText: `${t('cancel')}`
        }).then((result) => {
            if (result.isConfirmed) {
                handlerApp.setLoader(true);
                api.deleteMethod(endpoint + '/' + id)
                    .then(response => {
                        if (response.success) {
                            handlerApp.showOk(response);
                            sendRequest();
                        } else {
                            handlerApp.handlerResponse(response);
                        }
                    })
                    .catch(error => {
                        handlerApp.showError(t(error.message));
                    })
                    .finally(() => {
                        handlerApp.setLoader(false);
                    });
            }
        });
    };

    const sendRequest = (pageNumber = page, rows = perPage) => {
        handlerApp.setLoader(true);

        const params = new URLSearchParams({
            page: pageNumber,
            per_page: rows,
            serial: filterSerial,
            type: filterType,
            search: searchText
        });

        api.get(`${endpoint}?${params.toString()}`)
            .then(response => {
                if (response.success) {
                    setData(response.data.data);
                    setTotalRows(response.data.total);
                    setPage(response.data.page);
                    setPerPage(response.data.per_page);
                } else {
                    handlerApp.showError(
                        response?.message
                            ? t(response.message)
                            : t("unexpectedError")
                    );
                }
            })
            .catch(error => {
                handlerApp.showError(
                    error?.response?.data?.message
                        ? t(error.response.data.message)
                        : t("networkError")
                );
            })
            .finally(() => {
                handlerApp.setLoader(false);
            });
    };

    const loadFilters = () => {
        api.get(`${endpoint}/filters`)
            .then(response => {
                if (response.success) {
                    setFilters({
                        serial: response.data.machines.map(m => ({
                            code: m.machine_serial,
                            name: `${m.machine_serial} - ${m.machine_label ?? 'N/A'}`
                        })),
                        type: response.data.types.map(t => ({
                            code: t.actionlog_type,
                            name: t.actionlog_type
                        }))
                    });
                } else {
                    handlerApp.showError(
                        response?.message ? t(response.message) : t("unexpectedError")
                    );
                }
            })
            .catch(err => {
                handlerApp.showError(t("networkError"));
            });
    };

    const clearFilters = () => {
        setFilterSerial('');
        setFilterType('');
        setSearchText('');
        sendRequest(1);
    };

    return (
        <div className="app container">
            {/* <!-- titulo --> */}
            <div className="d-flex justify-content-between">
                <section className="section-title-page">
                    <h3 className="title-page">{title}</h3>
                    <p className="light">{sub_title}</p>
                </section>
            </div>
            {/* <!-- Menu interno --> */}
            <nav className="child-menu">
                {menus?.map(item => {
                    return <NavLink key={item.menu_children} to={"/" + item.menu_children_action} activeclassname="active">{item.menu_children}</NavLink>
                })}
            </nav>
            {/* <!-- Filtros --> */}
            <section className="filters">
                <h4>{t("accessLog.filters")}</h4>
                <div className="row align-items-start">
                    <div className="col-md-4 align-items-center justify-content-center">
                        <BoldSelect
                            title={t("actionLog.machine")}
                            name="serial"
                            value={filterSerial}
                            options={filters.serial}
                            onChange={e => {
                                const value = e.target.value;
                                setFilterSerial(value ? value.split(' - ')[0] : '');
                            }}
                        />
                    </div>

                    <div className="col-md-4">
                        <BoldSelect
                            title={t("actionLog.type")}
                            name="type"
                            value={filterType}
                            options={filters.type}
                            onChange={e => setFilterType(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <div className="input-icon input-search">
                            <input
                                type="text"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                placeholder={t("search")}
                            />
                            <span
                                className="material-symbols-outlined"
                                style={{ cursor: 'pointer' }}
                                onClick={handleSearch}
                                title={t("search")}
                            >
                                search
                            </span>
                        </div>
                    </div>
                    <div className="col-md-1 p-1    ">
                        <button
                            className="btn-table-actions"
                            onClick={clearFilters}
                            title={t("clearFilters")}
                        >
                            <span className="material-symbols-outlined">filter_alt_off</span>
                        </button>
                        <button className="btn-table-actions" onClick={() => { handlerAction(null, 'refresh') }}>
                            <span className="material-symbols-outlined">autorenew</span>
                        </button>
                    </div>
                </div>
            </section>
            {/* <!-- Tabla --> */}
            <DataTable
                columns={columns}
                data={data}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                onChangePage={page => sendRequest(page)}
                onChangeRowsPerPage={(rows, page) => sendRequest(page, rows)}
                selectableRows
                persistTableHead
            />
        </div>
    )
}

export default ActionLog;